import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import { tokens, withAlpha } from "../../Common/designTokens";
import { fetchFixSessionHealth } from "../../http/api";
import FilterChips from "../InventoryComponents/FilterChips";
import { utcToLocal } from "../../Utils/timeConversions";

const formatDate = (value) => {
  const local = utcToLocal(value);
  return local ? local.format("DDMMMYY HH:mm:ss") : "";
};

const normalizeSessionList = (data) => {
  if (!data) return [];
  const list = Array.isArray(data) ? data : [data];
  return list
    .filter((entry) => entry && entry.sessionId)
    .sort((a, b) => {
      const aTime = Date.parse(a?.responseTime);
      const bTime = Date.parse(b?.responseTime);
      if (isNaN(aTime) && isNaN(bTime)) return 0;
      if (isNaN(aTime)) return 1;
      if (isNaN(bTime)) return -1;
      return bTime - aTime;
    });
};

const QuickFixSessionStatus = ({ token, refreshTrigger = 0 }) => {
  const [sessionList, setSessionList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadSessionStatus = useCallback(() => {
    if (!token) {
      setSessionList([]);
      setError("");
      setLastUpdated(null);
      return;
    }
    setLoading(true);
    setError("");
    fetchFixSessionHealth(token)
      .then((data) => {
        const normalized = normalizeSessionList(data);
        setSessionList(normalized);
        setLastUpdated(normalized[0]?.responseTime || dayjs().toISOString());
      })
      .catch((err) => {
        setSessionList([]);
        setLastUpdated(null);
        setError(err?.message || "Unable to load session status.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    loadSessionStatus();
  }, [loadSessionStatus, refreshTrigger]);

  const sessionFilters = useMemo(() => {
    return sessionList.reduce((acc, session) => {
      if (session?.sessionId) {
        const displayStatus = session.fixSessionStatus === "UNKNOWN" ? "NA" : session.fixSessionStatus;
        acc[session.sessionId] = displayStatus;
      }
      return acc;
    }, {});
  }, [sessionList]);

  const sessionChipStyles = useCallback((key, value) => {
    const color =
      value === "LOGGED_IN"
        ? tokens.colors.buyFontColor
        : value === "NA"
          ? tokens.palette.danger
          : tokens.palette.primary;
    // Return only valueColor — FilterChips will color only the value text when provided
    return { valueColor: color };
  }, []);
  const hasSessionChips = Object.keys(sessionFilters).length > 0;
  const formattedTimestamp = lastUpdated ? formatDate(lastUpdated) : "";
  const sessionTimestampLabel = formattedTimestamp
    ? `Last updated: ${formattedTimestamp}`
    : loading
      ? "Last updated: checking session health…"
      : "Last updated: -";
  const fallbackStatusLabel = loading
    ? "Checking session status…"
    : error
      ? "Session status unavailable"
      : "No FIX sessions available";

  // RENDER: top row = heading + refresh + chips on same group; second row = last-updated
  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      width: "100%",
      minHeight: 44,
      pt: 1,
      pl: 2,
    }}>
      {/* Top row: heading + refresh + chips (all grouped left) */}
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: tokens.colors.fontColour, whiteSpace: "nowrap", mr: 0.5 }}>
            Session Details
          </Typography>

          <Tooltip title="Refresh session status">
            <span>
              <IconButton size="small" onClick={loadSessionStatus} disabled={loading} sx={{ p: 0.25 }}>
                {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>

          {/* Chips shown immediately after refresh */}
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
            {hasSessionChips ? (
              <FilterChips
                input={sessionFilters}
                onDeleteChip={() => {}}
                onClearFilters={() => {}}
                readOnly
                compact={true}
                maxVisibleChips={5}
                labelFormatter={(key) => key}
                chipSxResolver={sessionChipStyles}
                ladderName="Session Details"
              />
            ) : (
              <Typography variant="caption" sx={{ color: tokens.colors.fontColour85, whiteSpace: "nowrap", ml: 1 }}>
                {fallbackStatusLabel}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Second row: last-updated (below heading) */}
      <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ color: tokens.colors.fontColour85, whiteSpace: "nowrap" }}>
          {sessionTimestampLabel}
        </Typography>
        {error && (
          <Typography variant="caption" sx={{ color: tokens.palette.danger }}>
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default QuickFixSessionStatus;
